import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Unlock, X } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost, btnDanger } from "@/components/admin/ui";

export default function UsersAdminPage() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const fetchItems = () => apiClient.get("/users").then((r) => setItems(r.data));
    useEffect(() => { fetchItems(); }, []);

    const toggleSuspend = async (u) => {
        await apiClient.patch(`/users/${u.id}`, { suspended: !u.suspended });
        toast.success(u.suspended ? "User reinstated" : "User suspended");
        fetchItems();
    };
    const del = async (u) => {
        if (!window.confirm(`Delete ${u.email}?`)) return;
        await apiClient.delete(`/users/${u.id}`);
        fetchItems();
    };

    return (
        <>
            <PageHeader
                title="Users"
                subtitle="Super-admin only. Create editors, suspend or delete users."
                action={<button onClick={() => setOpen(true)} data-testid="add-user-btn" className={btnPrimary}><Plus size={16} /> Add user</button>}
            />
            <Card className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-sand dark:bg-stone-900 text-left text-xs uppercase tracking-widest text-stone-muted">
                        <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.map((u) => (
                            <tr key={u.id} className="border-t border-stone-border dark:border-stone-800" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3">{u.name}</td>
                                <td className="px-4 py-3 text-stone-muted">{u.email}</td>
                                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest bg-walnut/10 text-walnut px-2 py-1">{u.role}</span></td>
                                <td className="px-4 py-3">{u.suspended ? <span className="text-red-700 text-xs">Suspended</span> : <span className="text-green-700 text-xs">Active</span>}</td>
                                <td className="px-4 py-3 text-right space-x-2">
                                    {u.id !== me?.id && (
                                        <>
                                            <button onClick={() => toggleSuspend(u)} className={btnGhost}>{u.suspended ? <><Unlock size={12} /> Reinstate</> : <><Lock size={12} /> Suspend</>}</button>
                                            <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className={btnDanger}><Trash2 size={12} /></button>
                                        </>
                                    )}
                                    {u.id === me?.id && <span className="text-xs text-stone-muted">(you)</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {open && <NewUserModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); fetchItems(); }} />}
        </>
    );
}

function NewUserModal({ onClose, onSaved }) {
    const [form, setForm] = useState({ email: "", password: "", name: "", role: "editor" });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await apiClient.post("/auth/register", form);
            toast.success("User created");
            onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-cream dark:bg-[#171311] w-full max-w-md p-6 md:p-8" data-testid="new-user-form">
                <div className="flex justify-between mb-6"><h2 className="font-serif text-2xl">New user</h2><button type="button" onClick={onClose}><X /></button></div>
                <div className="space-y-4">
                    <Field label="Name" required><input data-testid="new-user-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Email" required><input data-testid="new-user-email" type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                    <Field label="Password" required><input data-testid="new-user-password" type="password" minLength={8} required className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
                    <Field label="Role">
                        <select data-testid="new-user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            <option value="editor">Editor (manage content)</option>
                            <option value="super_admin">Super admin (full access)</option>
                        </select>
                    </Field>
                </div>
                <div className="flex gap-3 mt-6">
                    <button className={btnPrimary} data-testid="new-user-save">Create user</button>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
